/**
 * CR-6322: Per-company ASN delivery-note column picker (company admins only).
 *
 * Seeded from the saved layout (which already lists every available column,
 * visible or not, in order). The admin toggles visibility and reorders; saving
 * persists the layout for the whole company, driving both the grid and the
 * printed delivery note. Reorder uses up/down controls — no drag-drop
 * dependency — so it stays keyboard-accessible and lightweight.
 *
 * CR-7194: also used by supplier admins, who configure their own ASN grid and
 * CSV export. The endpoint resolves the company from the requesting user, so
 * this component needs no notion of which side it is serving.
 *
 * `export_header` is carried through read -> save untouched. Saving replaces
 * the whole layout server-side, so dropping it here would silently wipe the
 * fixed ERP header names seeded by `seed_asn_export_layout` the first time an
 * admin reordered a column.
 */
import { useEffect, useState } from 'react';
import { X, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { purchasingAPI } from '../../lib/api';
import type { ASNColumnFormatColumn } from '../../lib/api';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { toast } from 'sonner';

interface ASNColumnPickerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful save so the parent can refetch the ASN. */
  onSaved: () => void;
}

type Row = Pick<ASNColumnFormatColumn, 'column_id' | 'label' | 'is_visible' | 'export_header'>;

export function ASNColumnPicker({ isOpen, onClose, onSaved }: ASNColumnPickerProps) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    purchasingAPI.getASNColumnFormat()
      .then((res) => {
        if (cancelled) return;
        const cols = [...res.data.columns].sort((a, b) => a.sort_order - b.sort_order);
        setRows(cols.map((c) => ({
          column_id: c.column_id,
          label: c.label,
          is_visible: c.is_visible,
          export_header: c.export_header ?? '',
        })));
      })
      .catch((err: unknown) => {
        if (!cancelled) toast.error(getApiErrorMessage(err, 'Failed to load column layout'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const toggle = (idx: number) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, is_visible: !r.is_visible } : r)));
  };

  const move = (idx: number, dir: -1 | 1) => {
    setRows((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await purchasingAPI.saveASNColumnFormat(
        rows.map((r, i) => ({
          column_id: r.column_id,
          is_visible: r.is_visible,
          sort_order: i,
          export_header: r.export_header ?? '',
        })),
      );
      toast.success('Delivery-note columns saved');
      onSaved();
      onClose();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to save column layout'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-base font-semibold">Delivery-note columns</h3>
            <p className="text-xs text-muted-foreground">Applies to every user at your company — grid, CSV export and print.</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-full transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <ul className="space-y-1">
              {rows.map((row, idx) => (
                <li key={row.column_id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted/40">
                  <input
                    type="checkbox"
                    checked={row.is_visible}
                    onChange={() => toggle(idx)}
                    className="h-4 w-4"
                    id={`asn-col-${row.column_id}`}
                  />
                  <label htmlFor={`asn-col-${row.column_id}`} className="flex-1 text-sm cursor-pointer">
                    {row.label}
                  </label>
                  <div className="flex flex-col">
                    <button
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                      title="Move up"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => move(idx, 1)}
                      disabled={idx === rows.length - 1}
                      className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                      title="Move down"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || loading} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
